'use client';
import React from 'react';

import styled from '@benzinga/themetron';
import { Note, NotesManager, NotesManagerEvent } from '@benzinga/notes-manager';
import Hooks from '@benzinga/hooks';
import { isString } from '@benzinga/utils';
import { StockSymbol } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { SafeType, SafePromise } from '@benzinga/safe-await';

import { TextAreaRef } from 'antd/lib/input/TextArea';
import { Input } from 'antd';
import { DateTime } from 'luxon';
import { CloseOutlined } from '@ant-design/icons';
import { Edit, Delete } from '@benzinga/themed-icons';

export interface NotesDialogProps {
  allowEdit: boolean;
  onClose: () => void;
  onUpdateNotes?: (changes: Note[]) => void;
  setNoteText?: (text: Note['text']) => void;
  symbol: StockSymbol;
}

interface State {
  notes: Note[];
}

const sortNotesOnDateCreatedDescending = (a: Note, b: Note) => {
  return DateTime.fromISO(b.getCreatedAt()).valueOf() - DateTime.fromISO(a.getCreatedAt()).valueOf();
};

const getCurrentSymbolNotes = (notes: Note[], symbol: StockSymbol): Note[] =>
  notes.filter(note => note.getSymbol() === symbol).sort(sortNotesOnDateCreatedDescending);

export const NotesDialog = React.memo<NotesDialogProps>(props => {
  const session = React.useContext(SessionContext);
  const notesManager = session.getManager(NotesManager);
  const notesArr = notesManager.getNotesFilteredStored(props.symbol);

  const [state, setState] = React.useState<State>({
    notes: getCurrentSymbolNotes(notesArr, props.symbol),
  });

  Hooks.useSubscriber(session.getManager(NotesManager), (event: NotesManagerEvent) => {
    switch (event.type) {
      case 'notes_updated':
        props.onUpdateNotes?.(event.notes);
        break;
    }
  });

  Hooks.useSubscriber(session.getManager(NotesManager), (event: NotesManagerEvent) => {
    switch (event.type) {
      case 'notes_updated': {
        props.onUpdateNotes?.(event.notes);
        const notes = getCurrentSymbolNotes(event.notes, props.symbol);
        setState(oldState => ({
          ...oldState,
          notes,
        }));
        break;
      }
    }
  });

  const updateNotes = React.useCallback(
    async (change?: NoteChange): SafePromise<Note> => {
      let note: SafeType<Note | undefined> = { ok: undefined };
      if (change) {
        switch (change.type) {
          case 'delete':
            note = await notesManager.deleteNote(change.note);
            break;
          case 'edit':
            note = await notesManager.updateNote(change.note, change.text);
            break;
          case 'new':
            note = await notesManager.createNote(change.symbol, change.text);
            break;
        }
      }

      const { ok: fetchedNotes = [] } = await notesManager.getNotesFiltered(props.symbol);
      const notes = fetchedNotes.sort(sortNotesOnDateCreatedDescending);
      const setNoteText = props.setNoteText;
      if (setNoteText && notes[0]) {
        setNoteText(notes[0].getText());
      }
      setState(oldState => ({ ...oldState, notes }));
      return note as SafeType<Note>;
    },
    [notesManager, props.symbol, props.setNoteText],
  );

  const onClose = () => {
    props.onClose();
  };

  const onNewNote = React.useCallback(
    async (text: string) => {
      if (text.length > 0) {
        const newItem: NoteChange = {
          symbol: props.symbol,
          text: text,
          type: 'new',
        };
        updateNotes(newItem);
      }
    },
    [props.symbol, updateNotes],
  );

  return (
    <NotesView>
      <ColumnContainer>
        <NotesHeader>
          <RowContainer>
            <div>Notes on {props.symbol}</div>
            {props.allowEdit && (
              <PointerCursor>
                <CloseOutlined onClick={onClose} title="Close" />
              </PointerCursor>
            )}
          </RowContainer>
        </NotesHeader>
        {props.allowEdit && <AddNote onNewNote={onNewNote} />}
        <NoteBody>
          {state.notes.map(note => (
            <NoteComponent allowEdit={props.allowEdit} key={note.getUuid()} note={note} onChange={updateNotes} />
          ))}
        </NoteBody>
      </ColumnContainer>
    </NotesView>
  );
});

interface AddNoteProps {
  onNewNote: (text: string) => void;
}

const AddNote: React.FC<AddNoteProps> = React.memo(({ onNewNote }) => {
  const [newText, setNewText] = React.useState('');
  const [placeholder, setPlaceholder] = React.useState('Type a new note...');
  const textboxRef = React.useRef<TextAreaRef>(null);

  React.useEffect(() => {
    textboxRef.current?.focus();
  }, []);

  const onNewNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewText(e.target.value);
  };

  const handleAddNote = () => {
    if (newText.length > 0) {
      onNewNote(newText);
      setNewText('');
      setPlaceholder('Type a new note...');
    } else {
      setPlaceholder('Note cannot be empty');
    }
  };

  return (
    <NotesAdd>
      <NoteAddBorder>
        <RowContainer>
          <Input.TextArea
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore old schema
            autoSize
            onChange={onNewNoteChange}
            onPressEnter={e => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleAddNote();
              }
            }}
            placeholder={placeholder}
            ref={textboxRef}
            value={newText}
          />
          <AddButtonContainer>
            <div className="AddButton" onClick={handleAddNote}>
              Add
            </div>
          </AddButtonContainer>
        </RowContainer>
      </NoteAddBorder>
    </NotesAdd>
  );
});

type NoteChange = EditNoteChange | DeleteNoteChange | NewNoteChange;

interface EditNoteChange {
  note: Note;
  text: string;
  type: 'edit';
}

interface DeleteNoteChange {
  note: Note;
  type: 'delete';
}

interface NewNoteChange {
  symbol: StockSymbol;
  text: string;
  type: 'new';
}

interface NoteProps {
  allowEdit: boolean;
  note: Note;
  onChange: (note: NoteChange) => void;
}

const NoteComponent: React.FC<NoteProps> = props => {
  const textboxRef = React.useRef<TextAreaRef>(null);
  const ogText = React.useRef(props.note.getText() ?? '');
  const [text, setText] = React.useState<string>(props.note.getText() ?? '');
  const [editingMode, setEditingMode] = React.useState(false);
  const dateCreated = DateTime.fromISO(props.note.getCreatedAt()).toFormat('ff');
  const dateUpdateTemp = props.note.getUpdatedAt();
  const dateUpdated = dateUpdateTemp ? DateTime.fromISO(dateUpdateTemp).toFormat('DD') : null;

  const noteText = props.note.getText();
  React.useEffect(() => {
    setText(noteText);
    ogText.current = noteText;
  }, [noteText]);

  React.useEffect(() => {
    if (editingMode) {
      textboxRef?.current?.focus();
    }
  }, [editingMode]);

  const onTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e?.target?.value;
    if (isString(val)) {
      setText(val);
    }
  };

  const onChanges = (_e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (text !== undefined) {
      ogText.current = text;
      props.onChange({ note: props.note, text, type: 'edit' });
    }
    setEditingMode(false);
  };

  const onCancel = (_e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setText(ogText.current);
    setEditingMode(false);
  };

  const onDelete = () => {
    props.onChange({ note: props.note, type: 'delete' });
  };

  if (props.allowEdit && editingMode) {
    return (
      <NoteColumnEditContainer className="NoteColumnEditContainer">
        <Input.TextArea // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore old schema
          autoSize
          onChange={onTextChange}
          ref={textboxRef}
          value={text ?? ''}
        />
        <div className="RowEditEndContainer">
          <div className="ActionButton" onClick={onCancel}>
            Cancel
          </div>
          <div className="ActionButton" onClick={onChanges}>
            Update
          </div>
        </div>
      </NoteColumnEditContainer>
    );
  } else {
    return (
      <NoteColumnContainer>
        <RowContainer>
          <NoteCreatedDate>
            {dateCreated}
            {props.note.getUpdatedAt() !== null && <NoteUpdatedDate> (edit on {dateUpdated})</NoteUpdatedDate>}
          </NoteCreatedDate>
          {props.allowEdit && (
            <NoteIcons>
              <MarginedIcon>
                <Edit onClick={() => setEditingMode(true)} title="Edit" />
              </MarginedIcon>
              <MarginedIcon>
                <Delete onClick={onDelete} title="Delete" />
              </MarginedIcon>
            </NoteIcons>
          )}
        </RowContainer>
        <NoteText>{text ?? ''}</NoteText>
      </NoteColumnContainer>
    );
  }
};

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 5px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const NoteText = styled.div`
  white-space: normal;
`;

const NoteColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const NoteColumnEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  margin-right: 10px;
  margin-top: 15px;

  .RowEditEndContainer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-top: 6px;
    gap: 5px;

    .ActionButton {
      background-color: ${props => props.theme.colors.brandMuted};
      color: ${props => props.theme.colors.foregroundActive};
      cursor: pointer;
      padding: 5px 10px;
      :hover {
        background-color: ${props => props.theme.colors.brand};
      }
    }
  }
`;

const NotesHeader = styled.div`
  background-color: ${props => props.theme.colors.backgroundActive};
  padding: 10px;
`;

const NoteBody = styled.div`
  background-color: ${props => props.theme.colors.background};
  display: flex;
  flex-direction: column;
  max-height: 500px;
  overflow: auto;
  padding-left: 10px;
  width: 350px;
`;

const NotesAdd = styled.div`
  background-color: ${props => props.theme.colors.background};
  margin-top: 2px;
`;

const NoteAddBorder = styled.div`
  border-bottom-width: 1px;
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  margin-bottom: 5px;
  padding: 5px;
`;

const NoteCreatedDate = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
  margin-top: 4px;
`;

const NoteUpdatedDate = styled.span`
  font-size: 0.75em;
  font-weight: normal;
  margin-bottom: 4px;
  margin-top: 4px;
`;

const NoteIcons = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 4px;
  margin-left: 4px;
  margin-right: 4px;
  margin-top: 4px;
`;

const MarginedIcon = styled.div`
  cursor: pointer;
  margin-left: 4px;
  margin-right: 4px;
`;

const NotesView = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  border-width: 2px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  .AddButton {
    background-color: ${props => props.theme.colors.brandMuted};
    color: ${props => props.theme.colors.foregroundActive};
    cursor: pointer;
    padding-bottom: 6px;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 6px;
    :hover {
      background-color: ${props => props.theme.colors.brand};
    }
  }
`;

const PointerCursor = styled.div`
  cursor: pointer;
`;
